// @flow

import React, { type Node } from 'react';
import { type Country } from '@tectonics/prs/country';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { useCurrentReceipt } from '@tectonics/prs/receipts';
import { fxToCurrencyString } from '@tectonics/prs/money';
import { Portal } from '@tectonics/commons/portal';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { IfFeature } from '@tectonics/prs/feature';
import { getReceiptFx } from '@tectonics/prs/receipts/receipt';

import { getMoneyFormat } from '../../../utils/money-format';
import { Tooltip } from '../../../molecules/tooltip';

export function ExchangeRate(): Node {
  const allFeatures = useAllFeatures();
  return(
    <IfFeature allFeatures={allFeatures} wanted="display_receipt_fx_rate">
      <ExchangeRateComponent />
    </IfFeature>
  );
}

function ExchangeRateComponent(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const currentReceipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatFx = fxToCurrencyString(decimalFormat);
  const formatedFx = formatFx(getReceiptFx(currentReceipt));

  const id = 'bromelia-exchange-rate-display';

  return (
    <Portal id={id}>
      <th className="total-line__name" scope="row">
        <span style={{ marginRight: 8 }}>
          <LabelText country={country} />
        </span>
        <div style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle', marginLeft: 8 }}>
          <TooltipContent country={country} formatedFx={formatedFx} />
        </div>
      </th>
      <td className="total-line__price">
        <span className="order-summary__emphasis">{formatedFx}</span>
      </td>
    </Portal>
  );
}

function LabelText(props: { country: Country }): Node {
  switch (props.country) {
    case 'br':
      return 'Taxa de câmbio';
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'uy':
    case 'py':
      return 'Tipo de Cambio';
    default:
      throw new Error(`Translation not found for "${props.country}"`);
  }
}

function TooltipContent(props: { country: Country, formatedFx: string }): Node {
  switch (props.country) {
    case 'br':
      return (
        <Tooltip insideField={false}>
          A Taxa de Câmbio é o preço de uma moeda estrangeira em relação a moeda local. <br/>
          <TooltipExchangeValue {...props} />
        </Tooltip>
      );
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'uy':
    case 'py':
      return (
        <Tooltip insideField={false}>
          El tipo de Cambio es el precio de una moneda extranjera en relación a la moneda local. <br />
          <TooltipExchangeValue {...props} />
        </Tooltip>
      );
    default:
      throw new Error(`Translation not found for "${props.country}"`);
  }
}

function TooltipExchangeValue(props: { country: Country, formatedFx: string }): Node {
  switch (props.country) {
    case 'ar':
      return <>US$&nbsp;1,00 = ARS{props.formatedFx}</>;
    case 'br':
      return <>US$&nbsp;1,00 = R{props.formatedFx}</>;
    case 'cl':
      return <>US$&nbsp;1,00 = CLP{props.formatedFx}</>;
    case 'co':
      return <>US$&nbsp;1,00 = COP{props.formatedFx}</>;
    case 'mx':
      return <>US$&nbsp;1,00 = MX{props.formatedFx}</>;
    case 'uy':
      return <>US$&nbsp;1,00 = UYU{props.formatedFx}</>;
    case 'py':
      return <>US$&nbsp;1,00 = PYG{props.formatedFx}</>;
    default:
      throw new Error(`Translation not found for "${props.country}" in TooltipExchangeValue`);
  }
}
